import { useState, useEffect } from "react";
import SECCard from "../../components/cards/SECCard/SECCard";
import SECDetail from "../../components/SECDetail/SECDetail";
import styles from "./ProductsRPDesigner.module.scss";
import ResponsePage from "./responsePage/ResponsePage";
import {
  templatenew,
  steps,
  componentTemplate,
} from "./responsePage/rptemplate";
import { useTour } from "@reactour/tour";
import SECTour from "../../components/SECTour/SECTour";
import SECTable from "../../components/SECTable/SECTable";
import GlobalSettings from "./globalSettings/GlobalSettings";
import ComponentSettings from "./componentSettings/ComponentSettings";
import rpService from "./rpService";
import ResponsePagePreview from "./responsePage/ResponsePagePreview";
import ResponsePageModal from "./responsePage/ResponsePageModal";
import { v4 as uuidv4 } from "uuid";

function ProductsRPDesigner({
  helpActive,
  toggleHelp,
  page = templatenew,
  setPage,
  setAttributes,
  attributes,
  createEnabled,
  selectedLanguage,
  setSelectedLanguage,
  getHtmlCode,
}) {
  const [colorFont, setColorFont] = useState(page.reactConfig.color.font);
  const [colorBackground, setColorBackground] = useState(
    page.reactConfig.color.background
  );
  const [gaps, setGaps] = useState(
    page.reactConfig.gaps ? page.reactConfig.gaps : templatenew.reactConfig.gaps
  );
  const [components, setComponents] = useState(page.reactConfig.components);

  const [selectedComponentIndex, setSelectedComponentIndex] = useState(-1);
  const [selectedDevice, setSelectedDevice] = useState(414);
  const [previewCode, setPreviewCode] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [openAttributes, setOpenAttributes] = useState(false);
  const [columnsAttributes, setColumnsAttributes] = useState([
    { field: "id", headerName: "ID", flex: 1, minWidth: 50 },
    { field: "label", headerName: "Name", flex: 1, minWidth: 100 },
    {
      field: "value",
      headerName: "Preview Value",
      flex: 5,
      minWidth: 400,
      editable: true,
    },
  ]);

  const [openLanguages, setOpenLanguages] = useState(false);
  const [columns, setColumns] = useState([
    { field: "id", headerName: "ID", flex: 1, minWidth: 50 },
    { field: "label", headerName: "Name", flex: 1, minWidth: 100 },
    {
      field: "en",
      headerName: "English",
      flex: 3,
      minWidth: 400,
      editable: true,
    },
  ]);

  /**
   * ***********************************************
   * hooks
   * ***********************************************
   */

  useEffect(() => {
    if (page?.reactConfig?.attributeDetails !== undefined) {
      setAttributes((prev) => {
        return prev.map((attr) => {
          const aDetail = page?.reactConfig?.attributeDetails?.find(
            (a) => a.label === attr.label
          );
          if (aDetail !== undefined) {
            return {
              ...attr,
              value: aDetail.value,
            };
          }

          return attr;
        });
      });
    }
  }, [openAttributes]);

  useEffect(() => {
    if (openLanguages === true) {
      getUsedLanguages().forEach((lang) => {
        if (columns.find((c) => c.field === lang) === undefined) {
          setColumns((prev) => {
            return [
              ...prev,
              {
                field: lang,
                headerName: rpService
                  .getLanguages()
                  .filter((cLang) => cLang.id === lang)[0]?.label,
                flex: 3,
                minWidth: 400,
                editable: true,
              },
            ];
          });
        }
      });
    }
  }, [openLanguages]);

  useEffect(() => {
    setPage((prev) => {
      return {
        ...prev,
        reactConfig: {
          ...prev.reactConfig,
          color: {
            font: colorFont,
            background: colorBackground,
          },
          gaps: gaps,
          components: components,
        },
      };
    });
  }, [colorFont, colorBackground, gaps, components]);

  /**
   * ***********************************************
   * functions
   * ***********************************************
   */

  function getUsedLanguages() {
    var foundLang = [];

    page.reactConfig.components.forEach((c) => {
      c.value.forEach((v) => {
        Object.keys(v.languages).forEach((key) => {
          if (key !== "en") foundLang.push(key);
        });
      });
    });

    return new Set(foundLang);
  }

  function InitializeTour() {
    const { setIsOpen } = useTour();

    useEffect(() => {
      setIsOpen(helpActive);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [helpActive]);

    return;
  }

  function previewPage() {
    const redirect = page.reactConfig.components.filter(
      (c) => c.name === "redirect"
    )[0];
    if (redirect) {
      window.open(redirect.value[0].languages[selectedLanguage]);
    } else {
      setPreviewCode(getHtmlCode(true));
      setOpenPreview(true);
    }
  }

  function changeAttributePreviewValue(newRow, row) {
    setPage((prev) => {
      return {
        ...prev,
        reactConfig: {
          ...prev.reactConfig,
          attributeDetails: prev.reactConfig.attributeDetails.map((p) => {
            if (p.label === row.label) {
              return {
                ...p,
                value: newRow.value,
              };
            }

            return p;
          }),
        },
      };
    });
  }

  function changeLanguageValueInComponents(newRow, row) {
    setComponents((prev) => {
      return prev.map((p, index) => {
        if (index === row.realId) {
          let values = [...p.value];
          values[row.vIndex] = {
            ...values[row.vIndex],
            languages: {
              ...values[row.vIndex].languages,
              [newRow.field]: newRow.value,
            },
          };

          return {
            ...p,
            value: values,
          };
        }

        return p;
      });
    });
  }

  function getRows() {
    var rows = [];
    var counter = 1;
    page.reactConfig.components.forEach((c, index) => {
      var row = {
        ...c,
      };

      c.value.forEach((v, i) => {
        var rowValues = {
          ...row,
        };

        Object.keys(v.languages).forEach((key) => {
          rowValues[key] = v.languages[key];
          rowValues["inputName"] = v.name;
          rowValues["id"] = counter++;
          rowValues["realId"] = index;
          rowValues["vIndex"] = i;
          rowValues.label = rowValues.label + " (" + v.label + ")";
        });

        rows.push({ ...rowValues });
      });
    });

    return rows;
  }

  function setPagename(pagename) {
    setPage((prev) => {
      return {
        ...prev,
        pagename: pagename,
        pagenamecap: pagename.toUpperCase(),
      };
    });
  }

  function handleComponentAttribute(value, name) {
    setComponents((prev) => {
      return prev.map((p, index) => {
        if (index === selectedComponentIndex) {
          return {
            ...p,
            [name]: value,
          };
        }

        return p;
      });
    });
  }

  function deleteComponent() {
    setComponents((prev) => {
      return prev.filter((p, index) => {
        return index !== selectedComponentIndex;
      });
    });

    setSelectedComponentIndex(-1);
  }

  function copyAll() {
    setComponents((prev) => {
      return prev.map((p) => {
        var values = p.value.map((value) => {
          return {
            ...value,
            languages: {
              ...value.languages,
              [selectedLanguage]: value.languages?.en || "",
            },
          };
        });

        return {
          ...p,
          value: values,
        };
      });
    });
  }

  function changeGlobalColorFont(value) {
    setColorFont((prev) => {
      return {
        ...prev,
        value: value,
      };
    });

    setAllComponentAttributes("fontColor", value);
  }

  function changeGlobalColorBackground(value) {
    setColorBackground((prev) => {
      return {
        ...prev,
        value: value,
      };
    });

    setAllComponentAttributes("backgroundColor", value);
  }

  function changeGap(value, name) {
    setGaps((prev) => {
      return {
        ...prev,
        [name]: {
          ...prev[name],
          value: value,
        },
      };
    });

    setAllComponentAttributes(name, value);
  }

  function setAllComponentAttributes(cName, value) {
    setComponents((prev) => {
      return prev.map((p) => {
        return {
          ...p,
          [cName]: value,
        };
      });
    });
  }

  function addComponent(name) {
    const t = componentTemplate[name];
    if (
      t !== undefined &&
      ((name !== "pin" && name !== "redirect") ||
        !isComponentAlreadyAdded(name))
    ) {
      setComponents((prev) => {
        return [
          ...prev,
          {
            ...t,
            id: uuidv4(),
          },
        ];
      });
    }
  }

  function isComponentAlreadyAdded(name) {
    const found = components.filter((c) => c.name === name);
    return found.length > 0;
  }

  function addImage(name, label) {
    setComponents((prev) => {
      return prev.map((p, index) => {
        if (index === selectedComponentIndex) {
          let values = [
            ...p.value,
            {
              name: name,
              label: label,
              languages: {
                [selectedLanguage]:
                  "https://codikett.ams3.digitaloceanspaces.com/securikett/logo/sec_signet.PNG",
              },
            },
          ];

          return {
            ...p,
            value: values,
          };
        }

        return p;
      });
    });
  }
  
  /**
   * ***********************************************
   * render
   * ***********************************************
   */

  return (
    <div
      className={"main--content--div " + styles["sec--products--rpdesigner"]}
    >
     
      <SECDetail
        open={openLanguages}
        handleClose={(e) => setOpenLanguages(false)}
        title="Languages"
        showSave={false}
      >
        <div style={{ height: "100%", width: "100%", padding: "40px" }}>
          <SECTable
            rows={getRows()}
            columns={columns}
            changeField={changeLanguageValueInComponents}
          />
        </div>
      </SECDetail>

      <SECDetail
        open={openAttributes}
        handleClose={(e) => setOpenAttributes(false)}
        title="Attributes"
        showSave={false}
      >
        <div style={{ height: "100%", width: "100%", padding: "40px" }}>
          <SECTable
            rows={attributes}
            columns={columnsAttributes}
            pageSize={100}
            changeField={changeAttributePreviewValue}
          />
        </div>
      </SECDetail>

      <SECTour toggleHelp={toggleHelp} helpActive={helpActive} steps={steps}>
        <InitializeTour />
      </SECTour>

      <SECCard cardWidth="10" cardHeight="100%" cardBackgroundColor="white">
        <GlobalSettings
          pagename={page.pagename}
          setPagename={setPagename}
          createEnabled={createEnabled}
          addComponent={addComponent}
          colorFont={colorFont}
          colorBackground={colorBackground}
          gaps={gaps}
          changeGlobalColorFont={changeGlobalColorFont}
          changeGlobalColorBackground={changeGlobalColorBackground}
          changeGap={changeGap}
          openLanguagesDetail={(e) => setOpenLanguages(true)}
          openAttributesDetail={(e) => setOpenAttributes(true)}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          copyAll={copyAll}
        />
      </SECCard>
      <SECCard
        cardWidth="40"
        cardHeight="100%"
        cardBackgroundColor="white"
        cardPadding="0 10px"
        cardOverflow="hidden"
      >
        <ResponsePagePreview
          selectedDevice={selectedDevice}
          setSelectedDevice={setSelectedDevice}
          previewPage={previewPage}
        />

        <ResponsePage
          colorFont={colorFont}
          colorBackground={colorBackground}
          components={components}
          setComponents={setComponents}
          language={selectedLanguage}
          setSelectedComponentIndex={setSelectedComponentIndex}
        />
      </SECCard>

      <SECCard cardWidth="10" cardHeight="100%" cardBackgroundColor="white">
        {components[selectedComponentIndex] && (
          <ComponentSettings
            components={components}
            handleComponentAttribute={handleComponentAttribute}
            deleteComponent={deleteComponent}
            addImage={addImage}
            setComponents={setComponents}
            selectedComponentIndex={selectedComponentIndex}
            selectedLanguage={selectedLanguage}
            attributes={attributes}
          />
        )}
      </SECCard>

      <ResponsePageModal
        openPreview={openPreview}
        setOpenPreview={setOpenPreview}
        selectedDevice={selectedDevice}
        previewCode={previewCode}
      />
    </div>
  );
}

export default ProductsRPDesigner;
